import {
  Box,
  Button,
  styled,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import IosShareIcon from "@mui/icons-material/IosShare";
import AdvancedSearch from "controls/global/advanced-search/AdvancedSearch";
import ColumnsConfig, {
  MenuColumnItem,
} from "controls/global/columns-config/ColumnsConfig";
import { FileSearchCriteria } from "entities/ApiModel/FileSearchCriteria";
import { FilesRecord } from "entities/UIModel";
import React, {
  RefObject,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  colors,
  defaultStyle,
  gaps,
  iconSize,
  padding,
} from "theme/defaultStyle";
import { useMyFiles } from "utils/context/MyFilesContext";
import {
  PageType, 
  PERMISSIONS, 
  SessionStorageKeys,
} from "utils/data/enum";
import { useProfileSettingsCache } from "utils/context/ProfileSettingsContext";

interface Props {
  columns: MenuColumnItem<FilesRecord>[];
  hiddenColumns: (keyof FilesRecord)[];
  updateHiddenColumns?: (columns: (keyof FilesRecord)[]) => void;
  handleAdvancedFilterChange: (
    name: keyof FileSearchCriteria,
    value: any,
    execSearch: boolean
  ) => void;
  handleAdvanceSearchPanelOpen: (open: boolean) => void;
  onExport?: () => void;
  storedCriteria :any;
}

const { button } = defaultStyle;

const Container = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: gaps.large1,
});

const ActionButtonGroup = styled(Box)({
  display: "flex",
  gap: gaps.large1,
  justifyContent: "flex-end",
  "& .MuiButton-root": {
    color: colors.blue01,
    display: "flex",
    gap: gaps.small1,
    paddingBottom: padding.xsmall1,
    paddingLeft: padding.xsmall2,
    paddingRight: padding.xsmall2,
    paddingTop: padding.xsmall1,
    textTransform: "none",
    "&.Mui-disabled": {
      background: colors.white,
      color: button.outlineSecondary.disabled.color,
    },
    "& .MuiSvgIcon-root": {
      fontSize: iconSize.medium,
    },
    "& .rotate-90": {
      transform: "rotate(90deg)"
    },
  },
});

const FilesPageHeader = ({
  columns,
  hiddenColumns,
  updateHiddenColumns,
  handleAdvancedFilterChange,
  handleAdvanceSearchPanelOpen,
  onExport,
  storedCriteria,
}: Props) => {
  const [advancedSearchOpen, setAdvancedSearchOpen] = useState<boolean>(false);
  const [, { setColumnDefinition }] = useMyFiles();
  const advancedButtonRef: RefObject<HTMLButtonElement> = useRef(null);
  
  // Permission
  const [{ profileSettings }] = useProfileSettingsCache();
  const permissionCodes = profileSettings?.userPermissionItemTypes as string[];

  const handleSearchPanel = (open: boolean) => {
    setAdvancedSearchOpen(open);
    handleAdvanceSearchPanelOpen(open);
  };

  const handleColumnChecked = (id: keyof FilesRecord, checked: boolean) => {
    if (checked) {
      updateHiddenColumns?.([...hiddenColumns, id]);
    } else {
      updateHiddenColumns?.([...hiddenColumns].filter((col) => col !== id));
    }

    setColumnDefinition([id], "hidden", checked);
  };

  const handleHideAllColumns = () => {
    if (hiddenColumns.length === columns.length) return;

    const fieldIds = columns.map((c) => c.field);
    updateHiddenColumns?.(fieldIds);
    setColumnDefinition(fieldIds, "hidden", true);
  };

  const handleShowAllColumns = () => {
    if (hiddenColumns.length === 0) return;

    const fieldIds = columns.map((c) => c.field);
    updateHiddenColumns?.([]);
    setColumnDefinition(fieldIds, "hidden", false);
  };

  const handleExport = () => {
    onExport?.();
  }

  useEffect(() => {
    const usePriorSearchCriteriaAdvanceSearch = sessionStorage.getItem(SessionStorageKeys.USE_PRIOR_SEARCH_CRITERIA_ADVANCE_SEARCH);

    if (usePriorSearchCriteriaAdvanceSearch && advancedButtonRef.current) {
      advancedButtonRef.current.click();
    }
  }, []);

  return (
    <Container>
      <ActionButtonGroup>
        <Button
          onClick={() => handleSearchPanel(!advancedSearchOpen)}
          data-test="myFilesAdvanced"
          ref={advancedButtonRef}
        >
          <>
            Advanced
            <SearchIcon />
          </>
        </Button>
        <ColumnsConfig
          options={columns}
          onChecked={handleColumnChecked}
          onHideAll={handleHideAllColumns}
          onShowAll={handleShowAllColumns}
        />
        <Button
          disabled={!permissionCodes?.includes(PERMISSIONS.EXPORT_DATA_PORTAL)}
          style={{
            opacity: 1
          }}
          onClick={() => handleExport()}
        >
          <>
            Export
            <IosShareIcon
              className="icon rotate-90" />
          </>
        </Button>        
      </ActionButtonGroup>
      <AdvancedSearch
        key={`adv_search_${advancedSearchOpen}`}
        open={advancedSearchOpen}
        handleFilterChange={advancedSearchOpen ? handleAdvancedFilterChange : () => { }}
        page={PageType.MyFilesPage}
        defaultValues={storedCriteria}
      />
    </Container>
  );
};

export default FilesPageHeader;
